/**
 * useLiteContext Hook
 *
 * Manages fetching and state for ServiceNow record context.
 * Now includes session-based authentication — creates a session
 * before loading ticket context.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { getServiceNowContext, listenForServiceNowContext } from '@/lib/utils';
import {
  fetchRecordContext,
  getMockContext,
  createSession,
  hasValidSession,
  type RecordContext,
  type ApiError,
} from '@/services/api';

interface UseLiteContextResult {
  context: RecordContext | null;
  loading: boolean;
  error: string | null;
  sysId: string | null;
  table: string;
  userId: string | null;
  userName: string | null;
  sessionReady: boolean;
  refetch: () => Promise<void>;
}

/**
 * Check if we should use mock data
 */
function shouldUseMock(): boolean {
  return import.meta.env.VITE_USE_MOCK === 'true';
}

export function useLiteContext(): UseLiteContextResult {
  const [context, setContext] = useState<RecordContext | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sessionReady, setSessionReady] = useState(shouldUseMock());

  // Get initial context from URL
  const { sysId: initialSysId, table: initialTable, userId: initialUserId, userName: initialUserName } = getServiceNowContext();
  const [sysId, setSysId] = useState<string | null>(initialSysId);
  const [table, setTable] = useState<string>(initialTable || 'incident');
  const [userId, setUserId] = useState<string | null>(initialUserId);
  const [userName, setUserName] = useState<string | null>(initialUserName);

  const sessionCreatingRef = useRef(false);
  const contextLoadedRef = useRef(false);

  /**
   * Fetch context from API or use mock
   */
  const fetchContext = useCallback(async () => {
    if (!sysId) {
      setLoading(false);
      setError('No record ID provided. Open this from a ServiceNow record.');
      return;
    }

    // Guard: don't make API calls without a valid session
    if (!shouldUseMock() && !hasValidSession()) {
      console.warn('[useLiteContext] fetchContext called without valid session, skipping');
      return;
    }

    // Prevent double loads
    if (contextLoadedRef.current) {
      console.log('[useLiteContext] Context already loaded, skipping');
      return;
    }
    contextLoadedRef.current = true;

    setLoading(true);
    setError(null);

    try {
      if (shouldUseMock()) {
        console.log('[useLiteContext] Using mock data');
        const mockData = getMockContext(sysId, table);
        setContext(mockData);
      } else {
        console.log(`[useLiteContext] Fetching context for ${table}/${sysId}`);
        const data = await fetchRecordContext(sysId, table);
        setContext(data);
      }
    } catch (err) {
      const apiError = err as ApiError;
      console.error('[useLiteContext] Error:', apiError.message);
      contextLoadedRef.current = false;  // Allow retry on error

      if (apiError.status === 401) {
        setError('Authentication failed. Please reload the widget.');
      } else if (apiError.status === 404) {
        setError('Record not found. Check the sys_id and table name.');
      } else if (apiError.status === 500 && import.meta.env.DEV) {
        console.log('[useLiteContext] Falling back to mock data');
        const mockData = getMockContext(sysId, table);
        setContext(mockData);
        setError(null);
      } else {
        setError(apiError.message || 'Failed to fetch record context');
      }
    } finally {
      setLoading(false);
    }
  }, [sysId, table]);

  /**
   * Attempt to create a session. Returns true if session is now valid.
   */
  const ensureSession = useCallback(async (uId: string, uName?: string, tSysId?: string, tbl?: string): Promise<boolean> => {
    if (shouldUseMock()) return true;
    if (hasValidSession()) return true;
    if (sessionCreatingRef.current) {
      console.log('[useLiteContext] Session creation already in progress, skipping');
      return false;
    }

    sessionCreatingRef.current = true;
    try {
      console.log('[useLiteContext] Creating session for user:', uId);
      await createSession({
        userId: uId,
        userName: uName || undefined,
        ticketSysId: tSysId || undefined,
        table: tbl || undefined,
      });
      setSessionReady(true);
      console.log('[useLiteContext] Session created successfully');
      return true;
    } catch (err) {
      console.error('[useLiteContext] Session creation failed:', err);
      setError('Authentication failed. Please reload the widget.');
      setLoading(false);
      return false;
    } finally {
      sessionCreatingRef.current = false;
    }
  }, []);

  /**
   * Manual refetch
   */
  const refetch = useCallback(async () => {
    if (sysId) {
      contextLoadedRef.current = false;
      await fetchContext();
    }
  }, [sysId, fetchContext]);

  /**
   * Listen for postMessage updates from ServiceNow
   */
  useEffect(() => {
    const cleanup = listenForServiceNowContext((newSysId, newTable, newUserId, newUserName) => {
      console.log(`[useLiteContext] Received postMessage: ${newTable}/${newSysId}, user: ${newUserId}`);
      setSysId(newSysId);
      setTable(newTable);
      if (newUserId) setUserId(newUserId);
      if (newUserName) setUserName(newUserName);

      // Create session if we got a userId from postMessage
      if (newUserId) {
        ensureSession(newUserId, newUserName || undefined, newSysId, newTable)
          .then((ok) => {
            if (ok && newSysId) {
              contextLoadedRef.current = false;
              fetchContext();
            }
          });
      }
    });

    return cleanup;
  }, [ensureSession, fetchContext]);

  /**
   * Initial load from URL params — runs once on mount
   */
  useEffect(() => {
    console.log('[useLiteContext] URL:', window.location.href);
    console.log('[useLiteContext] Parsed URL params:', { sysId: initialSysId, table: initialTable, userId: initialUserId, userName: initialUserName });

    if (shouldUseMock()) {
      // Dev mock mode — load immediately
      if (initialSysId) {
        fetchContext();
      } else {
        setLoading(false);
        setError('No record ID provided. Open this from a ServiceNow record.');
      }
      return;
    }

    if (initialUserId) {
      // Have userId — create session then load
      ensureSession(initialUserId, initialUserName || undefined, initialSysId || undefined, initialTable || undefined)
        .then((ok) => {
          if (ok && initialSysId) {
            fetchContext();
          } else if (ok && !initialSysId) {
            setLoading(false);
            setError('No record ID provided. Open this from a ServiceNow record.');
          }
        });
    } else {
      // No userId — check if we're in an iframe (likely SN context coming via postMessage)
      const isInIframe = window.self !== window.top;
      if (isInIframe) {
        console.log('[useLiteContext] No userId in URL, waiting for ServiceNow postMessage...');
        setLoading(true);

        const timeout = setTimeout(() => {
          if (!hasValidSession() && !sessionCreatingRef.current) {
            setLoading(false);
            setError('No user identity received. Please ensure the ServiceNow Client Script includes user_id parameter.');
          }
        }, 8000);

        return () => clearTimeout(timeout);
      } else {
        // Not in iframe and no userId — direct browser access
        setLoading(false);
        setError('Please open this widget from ServiceNow.');
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When session becomes ready and we have a sysId but no context data, load
  useEffect(() => {
    if (sessionReady && sysId && !context && !loading && !contextLoadedRef.current) {
      console.log('[useLiteContext] Session ready, loading context for:', sysId);
      fetchContext();
    }
  }, [sessionReady, sysId, context, loading, fetchContext]);

  return {
    context,
    loading,
    error,
    sysId,
    table,
    userId,
    userName,
    sessionReady,
    refetch,
  };
}
